import { ProjectEnumTextMapper, TaskEnumTextMapper } from "../../data_models/enumToTextMapper";
import { EProjectStatus } from "../../data_models/project";
import { ETaskStatus } from "../../data_models/task";
import { useEffect, useState } from "react";

// Define color classes explicitly so Tailwind won't purge them
const colorClasses = {
    yellow: {
        bg: 'bg-yellow-500',
        text: 'text-yellow-500',
        light: 'bg-yellow-100'
    },
    green: {
        bg: 'bg-green-500',
        text: 'text-green-500',
        light: 'bg-green-100'
    },
    red: {
        bg: 'bg-red-500',
        text: 'text-red-500',
        light: 'bg-red-100'
    },
    blue: {
        bg: 'bg-blue-500',
        text: 'text-blue-500',
        light: 'bg-blue-100'
    },
    purple: {
        bg: 'bg-purple-500',
        text: 'text-purple-500',
        light: 'bg-purple-100'
    },
    indigo: {
        bg: 'bg-indigo-500',
        text: 'text-indigo-500',
        light: 'bg-indigo-100'
    },
    pink: {
        bg: 'bg-pink-500',
        text: 'text-pink-500',
        light: 'bg-pink-100'
    },
    orange: {
        bg: 'bg-orange-500',
        text: 'text-orange-500',
        light: 'bg-orange-100'
    },
    teal: {
        bg: 'bg-teal-500',
        text: 'text-teal-500',
        light: 'bg-teal-100'
    },
    cyan: {
        bg: 'bg-cyan-500',
        text: 'text-cyan-500',
        light: 'bg-cyan-100'
    },
    emerald: {
        bg: 'bg-emerald-500',
        text: 'text-emerald-500',
        light: 'bg-emerald-100'
    },
    amber: {
        bg: 'bg-amber-500',
        text: 'text-amber-500',
        light: 'bg-amber-100'
    },
    lime: {
        bg: 'bg-lime-500',
        text: 'text-lime-500',
        light: 'bg-lime-100'
    },
    slate: {
        bg: 'bg-slate-500',
        text: 'text-slate-500',
        light: 'bg-slate-100'
    },
    gray: {
        bg: 'bg-gray-500',
        text: 'text-gray-500',
        light: 'bg-gray-100'
    }
};

export const TaskStatusBatch: React.FC<{ status?: ETaskStatus, variant?: 'text' | 'flat' }> = ({ status, variant = 'text' }) => {
    const [color, setColor] = useState<keyof typeof colorClasses>('purple');

    useEffect(() => {
        if (status === ETaskStatus.DRAFT) setColor('purple');
        else if (status === ETaskStatus.REQUESTED_APPROVAL) setColor('lime');
        else if (status === ETaskStatus.REJECTED_APPROVAL_REQUEST) setColor('gray');
        else if (status === ETaskStatus.WAIT_FOR_PAYMENT) setColor('cyan');
        else if (status === ETaskStatus.WAIT_FOR_FREELANCER) setColor('pink');
        else if (status === ETaskStatus.REASSIGN) setColor('amber');
        else if (status === ETaskStatus.IN_PROGRESS) setColor('yellow');
        else if (status === ETaskStatus.DELIVERY_PROVIDED) setColor('slate');
        else if (status === ETaskStatus.IN_REVISION) setColor('teal');
        else if (status === ETaskStatus.IN_REVISION_DELIVERY_PROVIDED) setColor('indigo');
        else if (status === ETaskStatus.DELIVERY_ACCEPTED) setColor('orange');
        else if (status === ETaskStatus.REQUESTED_COMPLETION) setColor('lime');
        else if (status === ETaskStatus.REJECTED_COMPLETION_REQUEST) setColor('yellow');
        else if (status === ETaskStatus.COMPLETED) setColor('green');
        else if (status === ETaskStatus.CANCELLED) setColor('red');
        else setColor('gray');
    }, [status]);

    return (
        <div className={`flex flex-row space-x-2 items-center ${variant === 'flat' ? `${colorClasses[color].light} px-1 py-0.2 rounded-md` : ''}`}>
            <div className={`rounded-full w-1 h-1 ${colorClasses[color].bg}`} />
            <p className={`text-sm ${colorClasses[color].text}`}>{TaskEnumTextMapper.taskStatus(status)}</p>
        </div>
    );
}

export const ProjectStatusBatch: React.FC<{ status?: EProjectStatus, variant?: 'text' | 'flat' }> = ({ status, variant = 'text' }) => {
    const [color, setColor] = useState<keyof typeof colorClasses>('blue');

    useEffect(() => {
        if (status === EProjectStatus.DRAFT) setColor('blue');
        else if (status === EProjectStatus.WAITING_FOR_PM) setColor('amber');
        else if (status === EProjectStatus.NOT_STARTED) setColor('purple');
        else if (status === EProjectStatus.NOT_STARTED_APPROVAL_REQUESTED) setColor('cyan');
        else if (status === EProjectStatus.NOT_STARTED_APPROVAL_REJECTED) setColor('teal');
        else if (status === EProjectStatus.IN_PROGRESS_REASSIGN_PM) setColor('yellow');
        else if (status === EProjectStatus.IN_PROGRESS) setColor('orange');
        else if (status === EProjectStatus.COMPLETION_REQUESTED) setColor('lime');
        else if (status === EProjectStatus.COMPLETION_REQUEST_REJECTED) setColor('indigo');
        else if (status === EProjectStatus.COMPLETED) setColor('green');
        else if (status === EProjectStatus.CANCELLED) setColor('red');
        else if (status === EProjectStatus.PAUSED) setColor('gray');
        else if (status === EProjectStatus.ARCHIVED) setColor('gray');
        else setColor('blue');
    }, [status]);

    return (
        <div className={`flex flex-row space-x-2 items-center ${variant === 'flat' ? `${colorClasses[color].light} px-1 py-0.2 rounded-md` : ''}`}>
            <div className={`rounded-full w-1 h-1 ${colorClasses[color].bg}`} />
            <p className={`text-sm ${colorClasses[color].text}`}>{ProjectEnumTextMapper.projectStatus(status)}</p>
        </div>
    );
}
